import { useWorkspace } from '@/features/workspaces/hooks';
import { AuthResponse, CustomConnectorDto } from '@/models/connectors';
import { useCompanyValues } from '@/providers';
import { audiencerateApi } from '@/services/audiencerate';
import { useCreateConnectorInstanceMutation } from '@/services/endpoints/workspace-connectors';
import { IChangeEvent } from '@rjsf/core';
import { RJSFSchema } from '@rjsf/utils';
import { FormEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';

const isAuthResponse = (response: unknown): response is AuthResponse =>
  typeof response === 'object' &&
  response !== null &&
  'action' in response &&
  (response as AuthResponse).action === 'PASS_TO_FRONTEND';

export const useSourceConnectorAuthConfig = (connector: CustomConnectorDto) => {
  const authConfig = connector.authConfiguration?.configurations?.[0]?.elements?.auth?.reactJsonSchemaForm;
  return {
    schema: authConfig?.schema,
    uiSchema: authConfig?.uiSchema ?? {},
  };
};

export const useSourceConnectorAuth = (connector: CustomConnectorDto) => {
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage: lang } = useCompanyValues();
  const dispatch = useDispatch();
  const [createInstance, { isLoading, error }] = useCreateConnectorInstanceMutation();

  const invalidateConnectors = useCallback(() => {
    dispatch(audiencerateApi.util.invalidateTags(['Connectors', 'ConnectorInstances']));
  }, [dispatch]);

  const openOAuthPopup = useCallback(
    (url: string) => {
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const popup = window.open(
        url,
        'oauthPopup',
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`
      );

      if (!popup) {
        alert('Please allow popups to open the authentication window');
        return;
      }

      const maxWait = 1000 * 90;
      const startTime = Date.now();

      const timer = setInterval(() => {
        if (popup.closed || Date.now() - startTime > maxWait) {
          if (!popup.closed) popup.close();
          clearInterval(timer);
          invalidateConnectors();
        }
      }, 500);
    },
    [invalidateConnectors]
  );

  const submitAuth = useCallback(
    async (data: IChangeEvent<any, RJSFSchema, any>, event: FormEvent<any>) => {
      event.preventDefault();

      try {
        const res = await createInstance({
          lang,
          workspaceId,
          connectorId: connector.id,
          name: data.formData?.['instance_name'] || `${connector.name} Connection`,
          authParams: data.formData ?? {},
        }).unwrap();

        if (isAuthResponse(res) && res.data?.['auth_url']) {
          openOAuthPopup(res.data['auth_url']);
        } else {
          invalidateConnectors();
        }

        return true;
      } catch (submitError) {
        console.error('Failed to create connector instance:', submitError);
        return false;
      }
    },
    [connector.id, connector.name, createInstance, invalidateConnectors, lang, openOAuthPopup, workspaceId]
  );

  return { submitAuth, isLoading, error };
};
